import React, { useContext } from 'react';
import { css } from '@emotion/react';
import {
  WAssetView,
  WBackgroundView,
  WBorderView,
} from 'components/publicWebsiteV2/mockData/theme_styles/types';
import { renderAbsoluteAssets } from 'components/publicWebsiteV2/util/renderHelpers';
import { mapPseudoElementToCSS, mapBackgroundToCSS } from 'components/publicWebsiteV2/util/mappers';
import styled from '@emotion/styled';
import {
  Z_INDEX_MAP,
  Z_INDEX_BORDER_LAYER,
  Z_INDEX_CONTENT_LAYER,
} from '../../util/zIndexConstants';
import { ThemeContext } from '../../context';

type BaseComponentProps = {
  assets?: WAssetView;
  className?: string;
  border?: WBorderView;
  background?: WBackgroundView;
  // needed for scooped
  containerBackgroundColor?: string;
  // for QA preview
  componentKey?: string;
};

/**
 * Container that handles element decoration, ie.
 * - assets: absolute and in flow
 */
const BaseComponent: React.FC<BaseComponentProps> = ({
  assets,
  border,
  background,
  containerBackgroundColor,
  className,
  componentKey,
  children,
}) => {
  const { highlightedComponents } = useContext(ThemeContext);
  const highlightOutline = highlightedComponents?.indexOf(componentKey || '') > -1;

  const { before, after, arrange } = assets?.inFlow || {};
  const displayStyle = `display: ${arrange === 'horizontal' ? 'inline-block' : 'block'}`;
  // resize margin/spacing accordingly

  const { shape, color: borderColor, thickness: borderThickness, pattern, spacing: borderSpacing } =
    border || {};

  const getBorderStyle = (s?: string) => {
    if (!s) return 'none';
    if (s === 'double') return 'solid';
    return s;
  };

  const CORNER_CIRCLE_SIZE = 8;

  const cornerCircle = css`
    z-index: ${Z_INDEX_MAP[Z_INDEX_BORDER_LAYER] + 1};
    content: '';
    position: absolute;
    width: ${CORNER_CIRCLE_SIZE}vw;
    height: ${CORNER_CIRCLE_SIZE}vw;
    /* ${borderThickness ? `border-width: ${borderThickness}px;` : ''}
    border-style: solid; */
    border-radius: 50%;
    transform: rotate(45deg);
    background-color: ${containerBackgroundColor};
  `;

  const Border = styled.div`
    position: relative;
    width: 100%;
    z-index: ${Z_INDEX_MAP[Z_INDEX_BORDER_LAYER]};
    border-style: ${getBorderStyle(pattern)};
    ${borderColor ? `border-color: ${borderColor};` : ''}
    ${borderThickness ? `border-width: ${borderThickness}px;` : ''}
    padding: ${(borderThickness || 0) + (borderSpacing || 0)}px; // space between + border width
    ${
      pattern === 'double'
        ? css`&:before {
        content: '';
        position: absolute;
        display: block;
        border-style: ${getBorderStyle(pattern)};
        ${borderColor ? `border-color: ${borderColor};` : ''}
        ${borderThickness ? `border-width: ${borderThickness}px;` : ''}
        z-index: ${Z_INDEX_MAP[Z_INDEX_BORDER_LAYER] - 1};
        top: ${borderSpacing}px;
        right: ${borderSpacing}px;
        left: ${borderSpacing}px;
        bottom: ${borderSpacing}px;
        }`
        : ''
    }

${
  shape === 'scooped_corner'
    ? css`&:before {
      ${cornerCircle}
      border-color: transparent ${borderColor} transparent transparent; //
      top: -${CORNER_CIRCLE_SIZE / 2}vw;
      left: -${CORNER_CIRCLE_SIZE / 2}vw;
    }
    &:after {
      ${cornerCircle}
      border-color: transparent transparent ${borderColor} transparent; //
      top: -${CORNER_CIRCLE_SIZE / 2}vw;
      right: -${CORNER_CIRCLE_SIZE / 2}vw;
    }`
    : ''
}

 `;

  const Border2 = styled.div`
    width: 100%;
    position: relative;
    ${shape === 'scooped_corner'
      ? css`&:after {
      ${cornerCircle}
      border-color: transparent transparent transparent ${borderColor}; //
      bottom: -${CORNER_CIRCLE_SIZE / 2}vw;
      right: -${CORNER_CIRCLE_SIZE / 2}vw;
    }
    &:before {
      ${cornerCircle}
      border-color: ${borderColor} transparent transparent transparent; //
      bottom: -${CORNER_CIRCLE_SIZE / 2}vw;
      left: -${CORNER_CIRCLE_SIZE / 2}vw;
    }`
      : ''}
  `;

  const Container = styled.div`
    ${displayStyle};
    position: relative;
    ${mapBackgroundToCSS(background)}

    &:before {
      ${displayStyle};
      margin-left: auto;
      margin-right: auto;
      vertical-align: middle;
      position: relative; // ALLOW OFFSET?
      ${mapPseudoElementToCSS(before)}
    }

    &:after {
      ${displayStyle};
      margin-left: auto;
      margin-right: auto;
      vertical-align: middle;
      ${mapPseudoElementToCSS(after)}
    }
    z-index: ${Z_INDEX_MAP[Z_INDEX_CONTENT_LAYER]};
  `;

  const Outline = styled.div`
    position: relative;
    border: ${highlightOutline ? '3px solid red' : 'none'};
    &:before {
      content: ${componentKey};
      display: ${highlightOutline ? 'block' : 'none'};
      position: absolute;
      top: 0;
      left: 0;
    }
  `;
  return (
    <Outline>
      <Border>
        <Border2>
          <Container className={className}>
            {renderAbsoluteAssets(assets)}
            {children}
          </Container>
        </Border2>
      </Border>
    </Outline>
  );
};

export default BaseComponent;
